import httpRequestHandler from '../utils/httpRequestHandler';

// Mirrors the API's risk tiers. Critical is reachable at a score of 85 but has
// never occurred in the cache, so the UI must render the tier without implying
// the absence of rows is a fault.
export const RISK_LEVEL = Object.freeze({
    CRITICAL: 'Critical',
    HIGH: 'High',
    ELEVATED: 'Elevated',
    LOWER: 'Lower Priority',
});

export const RISK_LEVEL_ORDER = Object.freeze([
    RISK_LEVEL.CRITICAL,
    RISK_LEVEL.HIGH,
    RISK_LEVEL.ELEVATED,
    RISK_LEVEL.LOWER,
]);

const _claimFraudCache = new Map();
const TTL_MS = 5 * 60 * 1000;

const _get = (key) => {
    const entry = _claimFraudCache.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
        _claimFraudCache.delete(key);
        return undefined;
    }
    return entry.value;
};

/**
 * Clears cached claim-fraud responses. Wired into signOut alongside the other
 * service caches so one user's claim data cannot be read by the next session
 * in the same tab.
 */
export function clearClaimFraudServiceCache() {
    _claimFraudCache.clear();
}

// Only keys the API accepts; anything else is dropped rather than sent and
// rejected. Names must match ClaimFraudService.#parseFilters on the API - an
// unrecognised key is ignored there, so a mismatch silently returns unfiltered
// data rather than erroring.
const FILTER_KEYS = [
    'startDate', 'endDate', 'riskLevel', 'minScore', 'flag', 'product',
    'productClassification', 'division', 'state', 'agentNo', 'covDesc',
    'memberSearch', 'deniedOnly',
];

const toParams = (filters = {}) =>
    FILTER_KEYS.reduce((acc, key) => {
        const value = filters[key];
        if (value !== undefined && value !== null && value !== '') acc[key] = value;
        return acc;
    }, {});

class ClaimFraudService {
    #fetch = async (path, filters, extraParams = {}) => {
        const params = { ...toParams(filters), ...extraParams };
        const key = `${path}:${JSON.stringify(params)}`;
        const cached = _get(key);
        if (cached !== undefined) return cached;

        const { data } = await httpRequestHandler.get(path, { params });
        const value = data?.data ?? data;
        _claimFraudCache.set(key, { value, expiresAt: Date.now() + TTL_MS });
        return value;
    };

    getKpi = (filters) => this.#fetch('/claim-fraud/kpi', filters);

    getCharts = (filters) => this.#fetch('/claim-fraud/charts', filters);

    // The API reads `sortColumn`; sending the view's `sortBy` name leaves it
    // undefined server-side, which silently falls back to risk-score order.
    getQueue = (filters, { page, pageSize, sortBy, sortDirection } = {}) =>
        this.#fetch('/claim-fraud/queue', filters, {
            page, pageSize, sortColumn: sortBy, sortDirection,
        });

    getMetadata = (name) => this.#fetch(`/claim-fraud/meta/${name}`, {});

    getClaimDetail = (claimIdx) => this.#fetch(`/claim-fraud/detail/${claimIdx}`, {});

    getStatus = () => this.#fetch('/claim-fraud/status', {});
}

const claimFraudService = new ClaimFraudService();

export default claimFraudService;
